import { useCallback, useMemo, useState } from 'react';

import { jwtDecode } from 'jwt-decode';

import { publicApi } from 'api';
import { useResendVerificationEmail } from 'api/hooks/public/auth/useResendVerificationEmail';

import { EmailVerificationErrorState, getEmailVerificationError } from 'utils/emailVerificationErrors';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

/**
 * Response shape from the email verification API endpoint.
 */
interface VerifyEmailResult {
  /** Indicates if the verification was successful */
  success: boolean;
  /** Additional data (currently null) */
  data: null;
}

/**
 * Return value from the useEmailVerification hook.
 */
interface UseEmailVerificationReturn {
  /** True when verification or resend request is in progress */
  loading: boolean;
  /** True when email has been successfully verified */
  success: boolean;
  /** Error state with user-friendly message and action, or null if no error */
  error: EmailVerificationErrorState | null;
  /** Email address extracted from the verification token, or null if token is invalid/missing */
  email: string | null;
  /** Function to initiate email verification. Shows error if token is missing/invalid. */
  verify: () => Promise<void>;
  /** Function to resend verification email. Shows error if email cannot be extracted from token. */
  resend: () => Promise<void>;
}

/**
 * Custom hook to handle email verification and resend logic.
 * Component controls when to call verify() - hook manages state and API calls.
 */
export function useEmailVerification(token: string | null): UseEmailVerificationReturn {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<EmailVerificationErrorState | null>(null);

  // resend verification email mutation
  const resendMutation = useResendVerificationEmail();

  // extract email from token using useMemo for optimization
  const email = useMemo(() => {
    if (!token) return null;

    try {
      const decoded = jwtDecode<{ email: string }>(token);
      return decoded.email ?? null;
    } catch (decodeError) {
      console.warn('Failed to decode token:', decodeError);
      return null;
    }
  }, [token]);

  const verify = useCallback(async () => {
    if (!token) {
      setError({
        severity: 'error',
        message: 'Verification link is missing required information. Please check the link in your email.',
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // POST request with email verification token
      await publicApi.post<VerifyEmailResult>(`/public/auth/email/verify`, {
        token,
      });

      // mark the verification as successful and clear any errors
      setSuccess(true);
    } catch (err) {
      setError(getEmailVerificationError(err));
    } finally {
      // stop the loading state
      setLoading(false);
    }
  }, [token]);

  const resend = useCallback(async () => {
    if (!email) {
      setError({
        severity: 'error',
        message: 'Unable to resend verification. The verification link is malformed or invalid.',
      });
      return;
    }

    try {
      await resendMutation.mutateAsync({ email });
      setError({
        severity: 'info',
        message: 'A new verification email has been sent. Please check your inbox.',
      });
    } catch (err) {
      setError({
        severity: 'error',
        message: getApiErrorMessage(err),
        actionTitle: 'Try Again',
        action: 'resend',
      });
    }
  }, [email, resendMutation]);

  return {
    loading: loading || resendMutation.isPending,
    success,
    error,
    email,
    verify,
    resend,
  };
}
